/**
 * Session Detail Blade Component
 * HIVE-6307: Unified blade with tabs for Details and Meter Values
 *
 * Header:
 * - Location: {locationName}
 * - Session ID: {sessionId}
 *
 * Tabs:
 * 1. Details Tab - 4 sections (Session Info, Payment Info, Charging Info, Tariff)
 * 2. Meter Values Tab - Chart and Grid views with sub-tabs
 */

import { useEffect, useState, useMemo } from 'react';
import { Zap, X, Download, Loader2, FileText, TableIcon, ChevronDown, ChevronRight } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/core/components/ui/sheet';
import { Button } from '@/core/components/ui/button';
import { Skeleton } from '@/core/components/ui/skeleton';
import { Checkbox } from '@/core/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/core/components/ui/collapsible';
import { SessionGridRow, SessionDetail } from '../types/session-types';
import { useSessionsReportStore } from '../stores/sessions-report-store';
import { SessionInfoSection } from './sections/SessionInfoSection';
import { PaymentInfoSection } from './sections/PaymentInfoSection';
import { ChargingInfoSection } from './sections/ChargingInfoSection';
import { TariffInfoSection } from './sections/TariffInfoSection';
import { MeterValuesGrid } from './MeterValuesGrid';
import { MeterValuesChart } from './MeterValuesChart';
import { parseCurrentAmps } from '../utils/meter-value-utils';

interface SessionDetailBladeProps {
  session: SessionGridRow | null;
  isOpen: boolean;
  onClose: () => void;
  /** Show current (A) instead of power (kW) in the chart and meter values. Defaults to kW. */
  unitMode?: 'A' | 'kW';
}

export function SessionDetailBlade({
  session,
  isOpen,
  onClose,
  unitMode = 'kW',
}: SessionDetailBladeProps) {
  const {
    selectedSessionDetail,
    isLoadingDetail,
    detailError,
    loadSessionDetail,
    clearSelectedSession,
    // Meter values state
    meterValues,
    meterValuesCount,
    meterValuesPage,
    meterValuesPageSize,
    meterValuesPageSizeOptions,
    isLoadingMeterValues,
    meterValuesError,
    isExportingMeterValues,
    filterEmptyDelta,
    loadMeterValuesPage,
    setMeterValuesPageSize,
    setFilterEmptyDelta,
    loadMeterValues,
    exportMeterValues,
  } = useSessionsReportStore();

  const [activeTab, setActiveTab] = useState<string>('details');
  const [isChartOpen, setIsChartOpen] = useState(true);

  // Determine if chart has meaningful data (used for initial collapse state)
  const hasChartData = useMemo(() => {
    if (!meterValues.length) return false;
    // Check if any meaningful data exists (same logic as MeterValuesChart)
    const hasEnergyData = meterValues.some((d) => d.deltaEnergy != null && d.deltaEnergy > 0);
    const hasPowerData = unitMode === 'A'
      ? meterValues.some((d) => parseCurrentAmps(d.current) != null)
      : meterValues.some((d) => d.power != null);
    const hasSocData = meterValues.some((d) => d.stateOfCharge != null && d.stateOfCharge > 0);
    return hasEnergyData || hasPowerData || hasSocData;
  }, [meterValues, unitMode]);

  // Auto-collapse chart when no data is available (after meter values load)
  useEffect(() => {
    if (!isLoadingMeterValues && meterValues.length > 0) {
      setIsChartOpen(hasChartData);
    }
  }, [isLoadingMeterValues, meterValues.length, hasChartData]);

  // Load session detail when blade opens
  useEffect(() => {
    if (session && isOpen) {
      loadSessionDetail(session);
    }
  }, [session, isOpen, loadSessionDetail]);

  // Load meter values when blade opens (needed for chart in Details tab)
  useEffect(() => {
    if (session && isOpen && meterValues.length === 0 && !isLoadingMeterValues) {
      loadMeterValues();
    }
  }, [session, isOpen, meterValues.length, isLoadingMeterValues, loadMeterValues]);

  // Reset to details tab when blade opens with new session
  useEffect(() => {
    if (isOpen) {
      setActiveTab('details');
    }
  }, [isOpen, session?.sessionId]);

  const handleClose = () => {
    clearSelectedSession();
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      {/* HIVE-6307: Unified blade with tabs - wider to accommodate meter values */}
      <SheetContent className="w-full sm:max-w-2xl lg:max-w-4xl xl:max-w-5xl overflow-hidden p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className="flex-shrink-0 bg-white z-10 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Zap className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <SheetTitle className="text-lg font-semibold text-gray-900">
                  Location: {session?.locationName ?? 'Unknown'}
                </SheetTitle>
                <SheetDescription className="text-sm text-gray-500">
                  Session ID: {session?.sessionId ?? '--'}
                </SheetDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Export button - only show on meter values tab */}
              {activeTab === 'meter-values' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                  onClick={exportMeterValues}
                  disabled={isExportingMeterValues || isLoadingMeterValues || meterValuesCount === 0}
                >
                  {isExportingMeterValues ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  Export
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={handleClose}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </SheetHeader>

        {/* No Session Selected */}
        {!session && (
          <div className="flex-1 flex items-center justify-center py-12">
            <div className="text-center">
              <p className="text-gray-500">No session selected</p>
              <p className="text-sm text-gray-400 mt-1">
                Select a session from the grid to view details
              </p>
            </div>
          </div>
        )}

        {/* Main Tabs - Details and Meter Values */}
        {session && (
          <div className="flex-1 min-h-0 flex flex-col">
            {/* Tab Buttons */}
            <div className="flex-shrink-0 px-6 pt-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground">
                  <button
                    type="button"
                    onClick={() => setActiveTab('details')}
                    className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all gap-2 ${
                      activeTab === 'details'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'hover:bg-background/50'
                    }`}
                  >
                    <FileText className="h-4 w-4" />
                    Details
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('meter-values')}
                    className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all gap-2 ${
                      activeTab === 'meter-values'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'hover:bg-background/50'
                    }`}
                  >
                    <TableIcon className="h-4 w-4" />
                    Meter Values
                    {meterValuesCount > 0 && (
                      <span className="ml-1 text-xs bg-gray-200 px-1.5 py-0.5 rounded-full">
                        {meterValuesCount}
                      </span>
                    )}
                  </button>
                </div>

                {/* Filter Empty Delta checkbox - only show on Meter Values tab */}
                {activeTab === 'meter-values' && (
                  <div className="flex items-center gap-1.5">
                    <Checkbox
                      id="filterEmptyDeltaBlade"
                      checked={filterEmptyDelta}
                      onCheckedChange={(checked) => setFilterEmptyDelta(checked === true)}
                      disabled={isLoadingMeterValues}
                    />
                    <label
                      htmlFor="filterEmptyDeltaBlade"
                      className="text-xs text-gray-600 cursor-pointer select-none"
                    >
                      Hide rows with Delta Energy = 0
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Details Tab Content */}
            {activeTab === 'details' && (
              <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
                {/* Loading State */}
                {isLoadingDetail && <BladeLoadingState />}

                {/* Error State */}
                {detailError && !isLoadingDetail && (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="text-center">
                      <p className="text-red-600 font-medium">
                        Failed to load session details
                      </p>
                      <p className="text-sm text-gray-500 mt-1">{detailError}</p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-4"
                        onClick={() => session && loadSessionDetail(session)}
                      >
                        Try Again
                      </Button>
                    </div>
                  </div>
                )}

                {/* Detail Content - Session Info + Chart + 3 other Sections */}
                {selectedSessionDetail && !isLoadingDetail && !detailError && (
                  <div className="space-y-4">
                    <SessionInfoSection detail={selectedSessionDetail} meterValues={meterValues} />

                    {/* Meter Values Chart - Collapsible, collapsed by default when no data */}
                    <Collapsible
                      open={isChartOpen}
                      onOpenChange={setIsChartOpen}
                      className="border border-gray-200 rounded-lg"
                    >
                      <CollapsibleTrigger className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-lg">
                        <h3 className="text-sm font-semibold text-gray-900">Session Chart</h3>
                        <div className="flex items-center gap-2">
                          {!hasChartData && !isLoadingMeterValues && (
                            <span className="text-xs text-gray-400">No data</span>
                          )}
                          {isChartOpen ? (
                            <ChevronDown className="h-4 w-4 text-gray-500" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-500" />
                          )}
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="px-4 pb-4">
                          <div className="h-[300px]">
                            <MeterValuesChart
                              data={meterValues}
                              isLoading={isLoadingMeterValues}
                              error={meterValuesError}
                              unitMode={unitMode}
                            />
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>

                    <PaymentInfoSection detail={selectedSessionDetail} />
                    <ChargingInfoSection detail={selectedSessionDetail} />
                    <TariffInfoSection detail={selectedSessionDetail} meterValues={meterValues} />
                  </div>
                )}
              </div>
            )}

            {/* Meter Values Tab Content - Grid Only */}
            {activeTab === 'meter-values' && (
              <div className="flex-1 min-h-0 flex flex-col">
                {/* Grid View */}
                <div className="flex-1 min-h-0 p-4">
                  <MeterValuesGrid
                    data={meterValues}
                    isLoading={isLoadingMeterValues}
                    error={meterValuesError}
                    unitMode={unitMode}
                    totalCount={meterValuesCount}
                    currentPage={meterValuesPage}
                    pageSize={meterValuesPageSize}
                    pageSizeOptions={meterValuesPageSizeOptions}
                    onPageChange={loadMeterValuesPage}
                    onPageSizeChange={setMeterValuesPageSize}
                    onRefresh={loadMeterValues}
                  />
                </div>

                {/* Summary Footer */}
                {meterValuesCount > 0 && (
                  <div className="flex-shrink-0 px-6 py-3 border-t border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <span>
                        Total: <strong>{meterValuesCount.toLocaleString()}</strong> meter values
                      </span>
                      {session && (
                        <span>
                          Energy: <strong>{session.totalEnergy?.toFixed(3) || '--'}</strong> kWh
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/**
 * Loading skeleton for the blade content
 */
function BladeLoadingState() {
  return (
    <div className="space-y-4">
      {/* Section skeletons */}
      {[1, 2, 3, 4].map((section) => (
        <div
          key={section}
          className="border border-gray-200 rounded-lg p-4 space-y-4"
        >
          <Skeleton className="h-5 w-32" />
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Skeleton className="h-3 w-20 mb-1" />
              <Skeleton className="h-4 w-24" />
            </div>
            <div>
              <Skeleton className="h-3 w-20 mb-1" />
              <Skeleton className="h-4 w-24" />
            </div>
            <div>
              <Skeleton className="h-3 w-20 mb-1" />
              <Skeleton className="h-4 w-24" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Skeleton className="h-3 w-20 mb-1" />
              <Skeleton className="h-4 w-24" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
